import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import { IcoUser } from "../components/icons";
import { AccountLayout } from "../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  ImageUpload,
  Input,
  useFocusTrap,
} from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";
import {
  currentUser,
  setCurrentUserName,
  setCurrentUserProfilePicture,
} from "../stores/current-user";
import { showError } from "../stores/errors";
import type { MimicState } from "../stores/mimic";
import type { User } from "../types";
import { getErrorMessage } from "../utils/errors";

type LoaderData = {
  user: User;
  mimic: MimicState;
};

export async function load(_args: LoaderArgs): Promise<LoaderData> {
  const { user, mimic } = await rpc.auth.me({});

  if (!user) {
    throw new RedirectError("/login");
  }

  return { user: user as User, mimic: mimic ?? null };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Account Settings");
  const { user, mimic } = state.value;

  return (
    <AccountLayout user={user} mimic={mimic} title="Account Settings">
      <div class="max-w-lg">
        <div class="flex flex-col">
          {/* Profile Picture Row */}
          <ProfilePictureRow
            imageUrl={currentUser.value?.profilePictureUrl ?? null}
            onUploadComplete={(url) => {
              setCurrentUserProfilePicture(url);
            }}
          />
          <div class="border-b border-border my-1" />

          {/* Name Row */}
          <NameRow initialName={user.name} />
          <div class="border-b border-border my-1" />

          {/* Email Row */}
          <EmailRow email={user.email} />
          <div class="border-b border-border my-1" />

          {/* Password Row */}
          <PasswordRow />
        </div>
      </div>
    </AccountLayout>
  );
}

// ============================================================================
// Profile Picture Row
// ============================================================================

function ProfilePictureRow({
  imageUrl,
  onUploadComplete,
}: {
  imageUrl: string | null;
  onUploadComplete: (url: string) => void;
}) {
  const showModal = useSignal(false);

  function openModal() {
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex items-center gap-4 py-3 px-2 w-full text-left hover:bg-surface-hover transition-colors rounded-lg"
      >
        <div class="size-12 rounded-full bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" class="size-full object-cover" />
          ) : (
            <IcoUser class="size-6 text-text-muted" />
          )}
        </div>
        <div class="flex flex-col">
          <span class="text-sm text-text-muted">Photo</span>
          <span class="text-text">
            {imageUrl ? "Change photo" : "Add a photo"}
          </span>
        </div>
      </button>

      {/* Photo Upload Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="photo-upload-title"
          data-brand="slate"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/60 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="px-5 py-4 border-b border-border">
              <h3
                id="photo-upload-title"
                class="text-lg font-semibold text-text"
              >
                Profile Photo
              </h3>
            </div>

            <div class="p-5">
              <ImageUpload
                label="Click to upload"
                helpText="PNG, JPG, or GIF up to 2MB"
                upload={{
                  getUploadUrl: () => rpc.files.getUserProfileUploadUrl({}),
                  confirmUpload: () => rpc.files.confirmUserProfileUpload({}),
                }}
                onUploadComplete={(path) => {
                  onUploadComplete(path);
                  closeModal();
                }}
              />
            </div>

            <div class="px-5 py-4 border-t border-border flex justify-end">
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Name Row
// ============================================================================

function NameRow({ initialName }: { initialName: string }) {
  const name = useSignal(initialName);
  const showModal = useSignal(false);
  const newName = useSignal(initialName);
  const isSaving = useSignal(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showModal.value && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showModal.value]);

  function openModal() {
    newName.value = name.value;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    isSaving.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSave() {
    const trimmedName = newName.value.trim();
    if (!trimmedName || trimmedName === name.value) {
      closeModal();
      return;
    }

    isSaving.value = true;
    try {
      await rpc.profile.updateProfile({ name: trimmedName });
      name.value = trimmedName;
      setCurrentUserName(trimmedName); // Update global state
      closeModal();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !isSaving.value) {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex flex-col gap-0.5 py-3 px-2 w-full text-left hover:bg-surface-hover transition-colors rounded-lg"
      >
        <span class="text-sm text-text-muted">Name</span>
        <span class="text-text">{name.value}</span>
      </button>

      {/* Name Edit Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="name-edit-title"
          data-brand="slate"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/60 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="px-5 py-4 border-b border-border">
              <h3 id="name-edit-title" class="text-lg font-semibold text-text">
                Edit Name
              </h3>
            </div>

            <div class="p-5">
              <input
                ref={inputRef}
                type="text"
                value={newName.value}
                onInput={(e) => {
                  newName.value = (e.target as HTMLInputElement).value;
                }}
                onKeyDown={handleKeyDown}
                placeholder="Your name"
                class="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-muted focus:outline-none focus:border-text-muted transition-colors"
                disabled={isSaving.value}
              />
            </div>

            <div class="px-5 py-4 border-t border-border flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={isSaving.value}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                disabled={isSaving.value || !newName.value.trim()}
                loading={isSaving.value}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Email Row
// ============================================================================

function EmailRow({ email }: { email: string }) {
  const showModal = useSignal(false);
  const newEmail = useSignal("");
  const isSending = useSignal(false);
  const emailSent = useSignal(false);
  const errors = useSignal<Record<string, string>>({});

  const formErrors = {
    get value() {
      return errors.value;
    },
    set value(v: Record<string, string>) {
      errors.value = v;
    },
  } as FormErrors;

  function openModal() {
    newEmail.value = "";
    errors.value = {};
    emailSent.value = false;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleSubmit(e: Event) {
    e.preventDefault();

    const trimmedEmail = newEmail.value.trim().toLowerCase();
    if (!trimmedEmail) {
      errors.value = { newEmail: "Email is required" };
      return;
    }

    if (trimmedEmail === email) {
      errors.value = {
        newEmail: "New email must be different from your current email",
      };
      return;
    }

    isSending.value = true;
    errors.value = {};

    try {
      await rpc.profile.requestEmailChange({ newEmail: trimmedEmail });
      emailSent.value = true;
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        const error = err as { message: string; code?: string; field?: string };
        if (error.field) {
          errors.value = { [error.field]: error.message };
        } else {
          errors.value = { newEmail: error.message };
        }
      } else {
        errors.value = { newEmail: "Something went wrong" };
      }
    } finally {
      isSending.value = false;
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex flex-col gap-0.5 py-3 px-2 w-full text-left hover:bg-surface-hover transition-colors rounded-lg"
      >
        <span class="text-sm text-text-muted">Email</span>
        <span class="text-text">{email}</span>
      </button>

      {/* Email Change Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-change-title"
          data-brand="slate"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/60 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="px-5 py-4 border-b border-border">
              <h3
                id="email-change-title"
                class="text-lg font-semibold text-text"
              >
                Change Email
              </h3>
            </div>

            {emailSent.value ? (
              <div class="p-5 text-center">
                <div class="text-4xl mb-4">📧</div>
                <p class="text-text mb-2">Confirmation email sent!</p>
                <p class="text-sm text-text-muted">
                  Check your inbox at <strong>{newEmail.value}</strong> and
                  click the link to confirm your new email address.
                </p>
              </div>
            ) : (
              <Form errors={formErrors} onSubmit={handleSubmit}>
                <div class="p-5 flex flex-col gap-4">
                  <Input
                    label="New email address"
                    name="newEmail"
                    type="email"
                    value={newEmail.value}
                    onInput={(e) => {
                      newEmail.value = (e.target as HTMLInputElement).value;
                    }}
                    placeholder="new@example.com"
                    autofocus
                  />
                  <p class="text-sm text-text-muted">
                    We'll send a confirmation link to your new email address.
                  </p>
                </div>
                <div class="px-5 py-4 border-t border-border flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSending.value}
                  >
                    Send
                  </Button>
                </div>
              </Form>
            )}

            {emailSent.value && (
              <div class="px-5 py-4 border-t border-border flex justify-end">
                <Button variant="secondary" onClick={closeModal}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Password Row
// ============================================================================

function PasswordRow() {
  const showModal = useSignal(false);
  const isSending = useSignal(false);
  const emailSent = useSignal(false);

  function openModal() {
    emailSent.value = false;
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
  }

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: showModal.value,
    onEscape: closeModal,
  });

  async function handleRequestPasswordChange() {
    isSending.value = true;

    try {
      await rpc.profile.requestPasswordChange({});
      emailSent.value = true;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSending.value = false;
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        class="flex flex-col gap-0.5 py-3 px-2 w-full text-left hover:bg-surface-hover transition-colors rounded-lg"
      >
        <span class="text-sm text-text-muted">Password</span>
        <span class="text-text">••••••••</span>
      </button>

      {/* Password Change Modal */}
      {showModal.value && (
        <div
          class="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-change-title"
          data-brand="slate"
        >
          {/* Backdrop */}
          <div
            class="absolute inset-0 bg-black/60 transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            ref={dialogRef}
            class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
          >
            <div class="px-5 py-4 border-b border-border">
              <h3
                id="password-change-title"
                class="text-lg font-semibold text-text"
              >
                Change Password
              </h3>
            </div>

            {emailSent.value ? (
              <>
                <div class="p-5 text-center">
                  <div class="text-4xl mb-4">📧</div>
                  <p class="text-text mb-2">Password reset email sent!</p>
                  <p class="text-sm text-text-muted">
                    Check your inbox for a link to set a new password.
                  </p>
                </div>
                <div class="px-5 py-4 border-t border-border flex justify-end">
                  <Button variant="secondary" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div class="p-5">
                  <p class="text-text-muted">
                    We'll send a secure link to your email address that will let
                    you set a new password.
                  </p>
                </div>
                <div class="px-5 py-4 border-t border-border flex justify-end gap-3">
                  <Button variant="secondary" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleRequestPasswordChange}
                    loading={isSending.value}
                  >
                    Send Link
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
